// Billing fixtures: invoices, invoice PDFs, credit balance, gift certificates,
// payment methods, and license refund. Michael Scott (DEMO_PERSON_ID) is a
// ~16-month Signature-tier subscriber — the richest license
// `~/types/licenses/licenses` models — with two cards on file, a small credit
// balance, and a paid invoice history.

import type { DemoRequest, DemoRoute } from '../types';
import { envelope } from '../envelope';
import { DEMO_PERSONA, demoDaysAgo } from '../persona';
import type { PaymentMethod } from '~/composables/usePaymentMethods';
import type { Invoice, InvoicesResponse } from '~/types/person/financial';
import type { BaseAPIResponse } from '~/types/api';
import { LicenseType, type RefundLicenseResponse } from '~/types/licenses/licenses';
import { licenseTierName } from '~/types/licenses/tiers';

const CURRENT_SIGNATURE_ANNUAL_PRICE = 79900; // $799.00 — Michael's real, current charge

function respondRefundLicense(): BaseAPIResponse<RefundLicenseResponse> {
    return envelope({
        status: 'refunded',
        amount: CURRENT_SIGNATURE_ANNUAL_PRICE,
        amount_formatted: '$799.00',
        refunded_at: demoDaysAgo(0),
        message: 'Your refund has been processed successfully.',
    });
}

// ── Invoices — ~16 months of paid history, most recent first ───────────────────────────────────

const INVOICE_SEEDS: Array<{ id: string; daysAgo: number; amount: number; description: string }> = [
    { id: 'inv_50231', daysAgo: 18, amount: 5000, description: 'Gift Certificate Purchase' },
    { id: 'inv_49188', daysAgo: 96, amount: 19700, description: 'Pro License — Family Gift (Annual)' },
    { id: 'inv_47650', daysAgo: 123, amount: CURRENT_SIGNATURE_ANNUAL_PRICE, description: 'Signature License — Annual Renewal' },
    { id: 'inv_45300', daysAgo: 214, amount: 2500, description: 'Gift Certificate Purchase' },
    { id: 'inv_43012', daysAgo: 305, amount: 19700, description: 'Pro License — Family Gift (Annual)' },
    { id: 'inv_39875', daysAgo: 487, amount: CURRENT_SIGNATURE_ANNUAL_PRICE, description: 'Signature License — Initial Purchase' },
];

const INVOICES: Invoice[] = INVOICE_SEEDS.map((seed) => {
    const date = demoDaysAgo(seed.daysAgo);
    return {
        id: seed.id,
        date,
        due_on: date,
        status: 'paid',
        amount: seed.amount,
        amount_due: 0,
        refunded: 0,
        description: seed.description,
        type: 'invoice',
    };
});

function respondInvoices(req: DemoRequest): InvoicesResponse {
    const page = Number(req.query.get('page')) || 1;
    const perPage = Number(req.query.get('per_page')) || 10;
    const start = (page - 1) * perPage;

    return {
        data: INVOICES.slice(start, start + perPage),
        current_page: page,
        last_page: Math.max(1, Math.ceil(INVOICES.length / perPage)),
        per_page: perPage,
        total: INVOICES.length,
    };
}

// downloadInvoice is typed as Promise<Blob> at the call site and fed straight into
// URL.createObjectURL — hand back a real (tiny, valid-looking) PDF blob rather than JSON.
function respondInvoiceDownload(req: DemoRequest): Blob {
    const invoiceId = req.match[2] ?? 'invoice';
    const body = `%PDF-1.4\n% Demo invoice ${invoiceId} for ${DEMO_PERSONA.full_name} — Privacy Bee\n%%EOF`;
    return new Blob([body], { type: 'application/pdf' });
}

// ── Credit balance & gift certificates ──────────────────────────────────────────────────────────

// Anything the demo mints during a session needs a unique handle. A counter rather than a random
// value, so the same sequence of actions always produces the same account — a demo that reads
// differently on the second run through is a demo nobody can rehearse.
let mintedSequence = 0;
const nextSequence = () => ++mintedSequence;

const DEMO_CREDIT_BALANCE = 42.5; // dollars — the wire unit this endpoint (and its callers) use

function respondCreditBalance() {
    return { data: { credit_balance: DEMO_CREDIT_BALANCE } };
}

/**
 * A minted code in the display form app-api hands out: `PB-`, six five-symbol Crockford groups and
 * a check symbol, 40 characters, so the demo renders and sends what a real purchase does. The `$`
 * is a check symbol the real alphabet has, chosen because it must travel URL-encoded; it is not
 * computed, as nothing in the demo redeems a code.
 */
const demoGiftCertificateCode = (sequence: number) =>
    `PB-D3M0G-1FTC0-DE5XA-MPN2K-9QRST-${String(sequence).padStart(5, '0')}-$`;

function respondGiftCertPurchase(req: DemoRequest) {
    const body = (req.body ?? {}) as { name?: string; email?: string; amount?: number };
    return {
        data: {
            code: demoGiftCertificateCode(nextSequence()),
            amount: body.amount ?? 50,
            name: body.name ?? DEMO_PERSONA.full_name,
            email: body.email ?? DEMO_PERSONA.email,
            status: 'completed',
            created_at: demoDaysAgo(0),
        },
    };
}

function respondGiftCertApply(req: DemoRequest) {
    const body = (req.body ?? {}) as { code?: string };
    return {
        data: {
            code: body.code ?? 'GIFT-DEMO01',
            amount: 25,
            applied: true,
            credit_balance: DEMO_CREDIT_BALANCE + 25,
        },
    };
}

function respondGiftCertEmail(req: DemoRequest) {
    const body = (req.body ?? {}) as { recipientName?: string; recipientEmail?: string };
    return { data: { sent: true, recipient_name: body.recipientName ?? '', recipient_email: body.recipientEmail ?? '' } };
}

// ── Payment methods ──────────────────────────────────────────────────────────────────────────────

// Mutable so PATCH/DELETE/primary/add actually change what the next GET returns — mirrors the
// identity/exposures fixtures' "mutate in place, read back the same state" contract.
let PAYMENT_METHODS: PaymentMethod[] = [
    {
        id: 'pm_demo_visa4242',
        type: 'card',
        brand: 'visa',
        last_four: '4242',
        exp_month: 11,
        exp_year: 2028,
        name: DEMO_PERSONA.full_name,
        link_email: null,
        is_primary: true,
    },
    {
        id: 'pm_demo_amex1005',
        type: 'card',
        brand: 'amex',
        last_four: '1005',
        exp_month: 3,
        exp_year: 2027,
        name: DEMO_PERSONA.full_name,
        link_email: null,
        is_primary: false,
    },
];

/**
 * The card `/me` reports. Identity fixtures read it from here rather than holding their own
 * copy: two claims about the same wallet disagree the moment a card is deleted, and the
 * gift-certificate flow gates on `/me` while the payment screen reads this collection.
 */
export function primaryPaymentMethod(): PaymentMethod | null {
    return PAYMENT_METHODS.find((pm) => pm.is_primary) ?? PAYMENT_METHODS[0] ?? null;
}

// Demo mode never mints a Stripe SetupIntent — ManagePaymentMethodsModal skips the Stripe SDK
// entirely in demo mode (see isDemoMode() there) and adds straight to fixture state below, so no
// fake client_secret is ever handed to Stripe's real elements()/confirmSetup().
function respondAddPaymentMethod(req: DemoRequest): { data: PaymentMethod } {
    const body = (req.body ?? {}) as { name?: string };
    const created: PaymentMethod = {
        id: `pm_demo_${nextSequence()}`,
        type: 'card',
        brand: 'visa',
        last_four: String(4310 + mintedSequence),
        exp_month: 12,
        exp_year: new Date().getFullYear() + 4,
        name: body.name || DEMO_PERSONA.full_name,
        link_email: null,
        is_primary: PAYMENT_METHODS.length === 0,
    };
    PAYMENT_METHODS = [...PAYMENT_METHODS, created];
    return { data: created };
}

export const billingRoutes: DemoRoute[] = [
    // Invoices — download (specific) before list (generic).
    { method: 'GET', pattern: /^people\/\d+\/financial\/invoices\/[^/]+$/, respond: respondInvoiceDownload },
    { method: 'GET', pattern: /^people\/\d+\/financial\/invoices$/, respond: respondInvoices },

    // Credit balance.
    { method: 'GET', pattern: /^people\/\d+\/financial\/credit-balance$/, respond: respondCreditBalance },

    // Gift certificates — apply/email (specific) before purchase (generic).
    { method: 'POST', pattern: /^people\/\d+\/financial\/gift-certificates\/apply$/, status: 201, respond: respondGiftCertApply },
    { method: 'POST', pattern: /^people\/\d+\/financial\/gift-certificates\/[^/]+\/email$/, respond: respondGiftCertEmail },
    { method: 'POST', pattern: /^people\/\d+\/financial\/gift-certificates$/, status: 201, respond: respondGiftCertPurchase },

    // Payment methods — /primary (specific) before the bare collection/item routes.
    // Both payers answer from the same fixtures: the company wallet is the same
    // collection API, and an unmatched route would have the demo modal report an
    // empty wallet and silently swallow every card it saved.
    {
        method: 'PUT',
        pattern: /^(?:people|companies)\/\d+\/financial\/payment-methods\/([^/]+)\/primary$/,
        respond: (req) => {
            const id = req.match[1];
            PAYMENT_METHODS.forEach((pm) => { pm.is_primary = pm.id === id; });
            return { data: null };
        },
    },
    {
        method: 'PATCH',
        pattern: /^(?:people|companies)\/\d+\/financial\/payment-methods\/([^/]+)$/,
        respond: (req) => {
            const pm = PAYMENT_METHODS.find((p) => p.id === req.match[1]);
            if (!pm) return { data: null };
            const body = (req.body ?? {}) as { exp_month?: number; exp_year?: number; cardholder_name?: string };
            if (body.exp_month) pm.exp_month = body.exp_month;
            if (body.exp_year) pm.exp_year = body.exp_year;
            if (body.cardholder_name) pm.name = body.cardholder_name;
            return { data: null };
        },
    },
    {
        method: 'DELETE',
        pattern: /^(?:people|companies)\/\d+\/financial\/payment-methods\/([^/]+)$/,
        respond: (req) => {
            const id = req.match[1];
            const removed = PAYMENT_METHODS.find((p) => p.id === id);
            PAYMENT_METHODS = PAYMENT_METHODS.filter((p) => p.id !== id);
            // Deleting the primary promotes the next card, the way the real backend would.
            if (removed?.is_primary && PAYMENT_METHODS.length) PAYMENT_METHODS[0].is_primary = true;
            return { data: null };
        },
    },
    { method: 'GET', pattern: /^(?:people|companies)\/\d+\/financial\/payment-methods$/, respond: () => ({ data: PAYMENT_METHODS }) },
    { method: 'POST', pattern: /^(?:people|companies)\/\d+\/financial\/payment-methods$/, status: 201, respond: respondAddPaymentMethod },

    // Licenses — the /change route (specific) before the bare license item route.
    { method: 'DELETE', pattern: /^people\/\d+\/licenses\/\d+\/change$/, respond: () => ({ data: null }) },
    { method: 'PATCH', pattern: /^people\/\d+\/licenses\/\d+$/, respond: () => ({ data: null }) },
    { method: 'DELETE', pattern: /^people\/\d+\/licenses\/\d+$/, respond: respondRefundLicense },
];
